import React, { useContext, useMemo, useCallback } from 'react';
import { get, isArray } from 'lodash';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import { mappedArray } from '@ubeya/shared/utils/array';
import { getTimesheetFieldsCompletedCount } from '@ubeya/shared/utils/timesheetFields';
import { CheckedRenderer, DocumentRenderer, TimesheetTaskFieldRenderer } from '@ubeya/shared-web/components';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import { FIELDS_MAP } from '@ubeya/shared-web/hooks/useFormFields';
import { getLeafChildrenData } from 'timeSheet/utils';

const EDITABLE_FIELD_TYPES = [
  FieldTypes.FIELD_TYPE_TEXT,
  FieldTypes.FIELD_TYPE_NUMBER,
  FieldTypes.FIELD_TYPE_DATE,
  FieldTypes.FIELD_TYPE_OPTIONS,
  FieldTypes.FIELD_TYPE_BOOLEAN
];

const useTimesheetFieldsColumns = ({
  showTimesheetFields = false,
  timesheetFields = [],
  updateTimesheetTaskField,
  amplitudeModuleName = 'Timesheet module'
} = {}) => {
  const { t, dateFormat = 'DD/MM/YYYY' } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);
  const canEditTimesheetFields = !!updateTimesheetTaskField && !!myPrivileges?.[privileges.TIMESHEET];

  const comparator = useCallback((valueA, valueB, nodeA, nodeB) => {
    if (nodeA?.group || nodeB?.group) {
      return 0;
    }
    const a = Number(valueA) || 0;
    const b = Number(valueB) || 0;
    return a - b;
  }, []);

  return useMemo(() => {
    if (!showTimesheetFields || !(timesheetFields || []).length) return [];

    const completedFieldsColumn = {
      colId: 'completedFields',
      headerName: t('sequencesResultsCompletedFieldsStatus'),
      width: 140,
      sortable: true,
      cellClass: 'justify-center',
      comparator,
      valueGetter: (params) => {
        if (params?.node?.group) {
          const groupItems = getLeafChildrenData(params.node) || [];
          return groupItems.reduce((total, item) => total + getTimesheetFieldsCompletedCount(item), 0);
        }
        if (params?.data?.summarized) return null;
        return getTimesheetFieldsCompletedCount(params.data);
      }
    };

    const fieldColumns = (timesheetFields || []).map((field) => {
      const fieldHeader = field.isDeleted ? `${field.name} (${t('deleted')})` : field.name;
      const fieldDataKey = `mappedTimesheetFieldsValues.${field.id}`;

      const mapOptions =
        field.options &&
        mappedArray(
          field.options,
          ({ id: optionId }) => optionId,
          ({ title }) => title
        );

      const valueGetter = (params) => get(params?.data, fieldDataKey);

      const formatValue = (value) => {
        if (value === undefined || value === null || value === '') return '';
        const formatted = FIELDS_MAP[field.fieldTypeId]?.viewFormatter?.(value, { format: dateFormat, mapOptions });
        if (formatted && isArray(formatted)) return formatted.join(',');
        if (formatted) return formatted;
        if (isArray(value)) return value.join(',');
        return value;
      };

      const valueFormatter = ({ value }) => formatValue(value);

      const baseCol = {
        colId: `timesheetField.${field.id}`,
        headerName: fieldHeader,
        sortable: true,
        initialHide: true,
        comparator,
        cellClass: 'justify-start',
        valueGetter
      };

      if ([FieldTypes.FIELD_TYPE_DOCUMENT, FieldTypes.FIELD_TYPE_SIGNATURE].includes(field.fieldTypeId)) {
        return {
          ...baseCol,
          cellRenderer: (params) => {
            if (params?.node?.group || params?.data?.summarized) return null;
            const value = valueGetter(params);
            if (!value) return null;
            // DocumentRenderer reads rowData[dataKey] (no dot-notation), so wrap extracted value.
            return <DocumentRenderer rowData={{ value }} dataKey="value" />;
          }
        };
      }

      if (EDITABLE_FIELD_TYPES.includes(field.fieldTypeId)) {
        return {
          ...baseCol,
          ...(field.fieldTypeId === FieldTypes.FIELD_TYPE_DATE ? { minWidth: 140 } : {}),
          cellRenderer: (params) => {
            if (params?.node?.group) return null;

            return (
              <TimesheetTaskFieldRenderer
                rowData={params.data}
                columnData={{
                  field,
                  onEdit: updateTimesheetTaskField,
                  canEdit: canEditTimesheetFields,
                  dateFormat,
                  formatValue,
                  amplitudeModuleName
                }}
              />
            );
          }
        };
      }

      if (field.fieldTypeId === FieldTypes.FIELD_TYPE_BOOLEAN) {
        return {
          ...baseCol,
          cellClass: 'justify-center',
          cellRenderer: (params) => {
            if (params?.node?.group || params?.data?.summarized) return null;
            return (
              <CheckedRenderer
                rowData={params.data}
                dataKey={fieldDataKey}
                showFalse
                showTooltipForFalse
                showDashSignForFalse
              />
            );
          }
        };
      }

      return {
        ...baseCol,
        valueFormatter,
        cellRendererParams: {
          tooltipBodyMaxWidth: 700
        }
      };
    });

    return [completedFieldsColumn, ...fieldColumns];
  }, [
    showTimesheetFields,
    timesheetFields,
    t,
    dateFormat,
    updateTimesheetTaskField,
    canEditTimesheetFields,
    amplitudeModuleName,
    comparator
  ]);
};

export default useTimesheetFieldsColumns;
